import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { createSupabaseWithAuth } from '../../../../Components/Utils/SupabaseConfig';
import { useSession } from '../../../../Components/Utils/SessionContext';
import SearchableSelectUI from './SearchableSelectUI';
import JobStatus from '../../../JobComponents/Utils/JobStatus';

const JobSearchSelect = ({
  label = "Select Job",
  placeholder = "Search for a job...",
  selectedJob,
  onJobSelect,
  onClear,
  disabled = false,
  clearable = true,
  searchDelay = 300,
  limit = 10
}) => {
  const { user } = useSession();
  const [searchTerm, setSearchTerm] = useState(selectedJob?.title || '');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Update search term when selectedJob changes
  useEffect(() => {
    if (selectedJob) {
      setSearchTerm(selectedJob.title || '');
    }
  }, [selectedJob]);

  // Function to fetch job suggestions from Supabase
  const fetchJobSuggestions = async (term) => {
    if (!term.trim() || term.length < 2) {
      setResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      // Check for user session
      if (!user || !user.access_token) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Create Supabase client with auth
      const supabaseWithAuth = createSupabaseWithAuth(user.access_token);
      
      // Make the search query
      const { data, error } = await supabaseWithAuth
        .from('jobs')
        .select('job_id, job_name, company_name, job_category_json, status')
        .eq('uid', user.id) // Filter by user ID
        .or(`job_name.ilike.%${term}%,company_name.ilike.%${term}%`)
        .limit(limit);
      
      if (error) {
        console.error('Job search error:', error);
        setResults([]);
        return;
      }
      
      if (!data || data.length === 0) {
        setResults([]);
        return;
      }
      
      // Format the results
      const formattedResults = data.map(item => ({
        job_id: item.job_id,
        title: item.job_name,
        company: item.company_name,
        job_category_json: item.job_category_json,
        status: item.status
      }));
      
      setResults(formattedResults);
    } catch (error) {
      console.error('Error searching for jobs:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Create debounced search function
  const debouncedFetchJobs = useMemo(
    () => debounce(fetchJobSuggestions, searchDelay),
    [user, searchDelay]
  );
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length >= 2) {
      debouncedFetchJobs(term);
    } else {
      setResults([]);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchJobs.cancel();
    };
  }, [debouncedFetchJobs]);

  // Handle job selection
  const handleJobSelect = (job) => {
    onJobSelect(job);
    setSearchTerm(job.title);
    setResults([]);
  };

  // Handle clear
  const handleClear = () => {
    onClear();
    setSearchTerm('');
    setResults([]);
  };

  return (
    <SearchableSelectUI
      id="jobSearch"
      label={label}
      placeholder={placeholder}
      searchTerm={searchTerm}
      onSearchChange={handleSearchChange}
      results={results}
      isSearching={isSearching}
      selectedItem={selectedJob}
      onItemSelect={handleJobSelect}
      onClear={handleClear}
      disabled={disabled}
      clearable={clearable}
      renderItem={(job) => (
        <div className="cursor-pointer">
          <div className="flex justify-between items-center">
            <div className="font-medium">{job.title}</div>
            <div className="pr-8">
              <JobStatus status={job.status} size="sm" />
            </div>
          </div>
          {job.company && <div className="text-sm text-gray-600">{job.company}</div>}
          {job.job_category_json && job.job_category_json.subCategory && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-medium">{job.job_category_json.subCategory}</span>
              {job.job_category_json.mainCategory && (
                <span> - {job.job_category_json.mainCategory}</span>
              )}
              {job.job_category_json.altSubcategory && (
                <span className="italic ml-1">({job.job_category_json.altSubcategory})</span>
              )}
            </div>
          )}
        </div>
      )}
      renderSelectedItem={(job) => (
        <div>
          <div className="flex justify-between items-center">
            <div className="font-medium">{job.title}</div>
            <div className="pr-8">
              <JobStatus status={job.status} size="sm" />
            </div>
          </div>
          {job.company && <div className="text-sm text-gray-600">{job.company}</div>}
          {job.job_category_json && job.job_category_json.subCategory && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-medium">{job.job_category_json.subCategory}</span>
              {job.job_category_json.mainCategory && (
                <span> - {job.job_category_json.mainCategory}</span>
              )}
              {job.job_category_json.altSubcategory && (
                <span className="italic ml-1">({job.job_category_json.altSubcategory})</span>
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default JobSearchSelect;
